/**
 * pwa-atkomst booking client — mirrors src/domains/booking/api/bookingApi.ts
 * but targets this Next.js app's Supabase client instance.
 */

import { supabase } from './supabase';

export interface BookingResource {
  id: string;
  asset_id: string | null;
  name: string;
  resource_type: string;
  capacity: number;
  timezone: string;
  is_active: boolean;
  is_public_bookable: boolean;
  public_slug: string | null;
}

export interface BookingRow {
  id: string;
  asset_id: string;
  user_id: string;
  from_ts: string;
  to_ts: string;
  status: 'reserved' | 'paid' | 'active' | 'ended' | 'canceled';
  payment_id: string | null;
  issued_key_ids: string[] | null;
  created_at: string;
}

export interface PriceQuote {
  amount: number;
  currency: string;
  deposit: number;
  pricing_type: string;
}

async function invoke<T>(subpath: string, opts: { method: 'GET' | 'POST'; body?: Record<string, unknown> }) {
  const { data, error } = await supabase.functions.invoke<T>(`api-bookings${subpath}`, {
    method: opts.method,
    body: opts.body,
  });
  if (error) throw error;
  return data as T;
}

export const bookingApi = {
  myBookings: () =>
    invoke<{ bookings: BookingRow[]; total: number }>('/bookings?mine=true&limit=50', { method: 'GET' }),

  listResources: () =>
    invoke<{ resources: BookingResource[] }>('/resources', { method: 'GET' }),

  availability: (resource_id: string, from: string, to: string) => {
    const qs = new URLSearchParams({ resource_id, from, to });
    return invoke<{
      resource: { id: string; capacity: number; timezone: string; is_public_bookable: boolean };
      window: { from: string; to: string };
      occupied: Array<{ booking_id: string; from_ts: string; to_ts: string; status: string }>;
      price: PriceQuote | null;
    }>(`/availability?${qs.toString()}`, { method: 'GET' });
  },

  createBooking: (input: { resource_id: string; from_ts: string; to_ts: string; notes?: string }) =>
    invoke<{ booking_id: string; status: 'reserved'; price: PriceQuote | null }>(
      '/bookings',
      { method: 'POST', body: input },
    ),

  cancelBooking: (booking_id: string, reason?: string) =>
    invoke<{ booking_id: string; status: 'canceled' }>(
      `/bookings/${booking_id}/cancel`,
      { method: 'POST', body: { reason } },
    ),
};
